import React, { memo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  Divider,
  TextField,
  Chip,
  Alert,
} from '@mui/material';
import {
  Edit as EditIcon,
  Publish as PublishIcon,
  Archive as ArchiveIcon,
} from '@mui/icons-material';
import type { ServiceOffering } from '../../../types/backend';
import OfferingStatusChip from './OfferingStatusChip';
import OfferingForm from './OfferingForm';
import CapabilitiesPanel from './CapabilitiesPanel';
import ConfirmDialog from '../../../shared/components/ConfirmDialog';
import type { ServiceOfferingCreateRequest, ServiceOfferingUpdateRequest } from '../services/serviceOfferingsService';
import { categoryLabel } from '../../../shared/labels';

interface OfferingDetailPanelProps {
  offering: ServiceOffering;
  onUpdate: (id: string, request: ServiceOfferingUpdateRequest) => Promise<ServiceOffering>;
  onPublish: (id: string, version: string) => Promise<ServiceOffering>;
  onDeprecate: (id: string) => Promise<ServiceOffering>;
  onOfferingUpdated: (updated: ServiceOffering) => void;
}

const OfferingDetailPanel: React.FC<OfferingDetailPanelProps> = memo(({
  offering,
  onUpdate,
  onPublish,
  onDeprecate,
  onOfferingUpdated,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [showDeprecateDialog, setShowDeprecateDialog] = useState(false);
  const [publishVersion, setPublishVersion] = useState('');

  const handleSaveMetadata = async (data: ServiceOfferingCreateRequest | ServiceOfferingUpdateRequest) => {
    setSaving(true);
    setError(null);
    try {
      const updated = await onUpdate(offering.id, data as ServiceOfferingUpdateRequest);
      onOfferingUpdated(updated);
      setIsEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!publishVersion.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await onPublish(offering.id, publishVersion.trim());
      onOfferingUpdated(updated);
      setShowPublishDialog(false);
      setPublishVersion('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Publish failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDeprecate = async () => {
    setSaving(true);
    setError(null);
    try {
      const updated = await onDeprecate(offering.id);
      onOfferingUpdated(updated);
      setShowDeprecateDialog(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Deprecate failed');
    } finally {
      setSaving(false);
    }
  };

  const offeringStatus = offering.status ?? 'draft';
  const canPublish = offeringStatus === 'draft' && (offering.capabilities?.length ?? 0) > 0;
  const canDeprecate = offeringStatus === 'active';
  const isReadonly = offeringStatus === 'deprecated';

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3, height: '100%', overflow: 'auto' }}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="h6">{offering.display_name}</Typography>
            <OfferingStatusChip status={offering.status ?? 'draft'} />
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            title={offering.name}
          >
            {offering.name}{offering.version && offering.version !== '0.0.0' ? ` · v${offering.version}` : ''}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          {!isReadonly && !isEditing && (
            <Button size="small" startIcon={<EditIcon />} onClick={() => setIsEditing(true)} disabled={saving}>
              Edit
            </Button>
          )}
          {canPublish && !isEditing && (
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={<PublishIcon />}
              onClick={() => setShowPublishDialog(true)}
              disabled={saving}
            >
              Publish
            </Button>
          )}
          {canDeprecate && !isEditing && (
            <Button
              size="small"
              color="warning"
              startIcon={<ArchiveIcon />}
              onClick={() => setShowDeprecateDialog(true)}
              disabled={saving}
            >
              Deprecate
            </Button>
          )}
        </Stack>
      </Box>

      <Divider />

      {isEditing ? (
        <OfferingForm
          offering={offering}
          isNew={false}
          saving={saving}
          onSave={handleSaveMetadata}
          onCancel={() => setIsEditing(false)}
        />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {offering.description && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">Description</Typography>
              <Typography variant="body2">{offering.description}</Typography>
            </Box>
          )}
          {offering.owner && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">Owner</Typography>
              <Typography variant="body2">{offering.owner}</Typography>
            </Box>
          )}
          {offering.category && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">Category</Typography>
              <Typography variant="body2">{categoryLabel(offering.category)}</Typography>
            </Box>
          )}
          {offering.cloud_platforms && offering.cloud_platforms.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>Platforms</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {offering.cloud_platforms.map(p => <Chip key={p} label={p} size="small" />)}
              </Box>
            </Box>
          )}
        </Box>
      )}

      {!isEditing && (
        <>
          <Divider />
          <CapabilitiesPanel
            offering={offering}
            onOfferingUpdate={onOfferingUpdated}
            readonly={isReadonly}
          />
        </>
      )}

      <ConfirmDialog
        open={showPublishDialog}
        title={`Publish version ${publishVersion || '…'}`}
        onConfirm={handlePublish}
        onCancel={() => { setShowPublishDialog(false); setPublishVersion(''); }}
        confirmText={publishVersion.trim() ? `Publish ${publishVersion.trim()}` : 'Publish'}
        confirmColor="primary"
      >
        <Stack spacing={2}>
          <TextField
            label="Version"
            value={publishVersion}
            onChange={e => setPublishVersion(e.target.value)}
            size="small"
            placeholder="e.g. 1.0.0"
            fullWidth
            autoFocus
            helperText="Semantic version for this release (e.g. 1.0.0)"
          />
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Publishing makes version {publishVersion.trim() || '…'} the active version, so customers can subscribe to this offering.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Subscribers track the current offering — editing and re-publishing changes what they receive on the next sync.
            </Typography>
          </Box>
        </Stack>
      </ConfirmDialog>

      <ConfirmDialog
        open={showDeprecateDialog}
        title="Deprecate Offering?"
        onConfirm={handleDeprecate}
        onCancel={() => setShowDeprecateDialog(false)}
        confirmText="Deprecate"
        confirmColor="warning"
      >
        <Typography variant="body2">
          Deprecating &ldquo;{offering.display_name}&rdquo; marks it as no longer recommended.
          Existing customer subscriptions are not affected. This action cannot be undone.
        </Typography>
      </ConfirmDialog>
    </Box>
  );
});

OfferingDetailPanel.displayName = 'OfferingDetailPanel';

export default OfferingDetailPanel;
